/**
 * Condition evaluation — the one matching primitive.
 *
 * Branching, disqualification, scoring, and value-card gating all reduce to
 * "does this ConditionGroup match the collected answers?" A group is a list of
 * conditions ANDed together; OR is expressed as multiple rules. See types.ts.
 */
import type { Answers, Condition, ConditionGroup } from "../types";

type Scalar = string | number | boolean;

function isEmpty(v: unknown): boolean {
  if (v === undefined || v === null || v === "") return true;
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/** Collapse an answer to a comparable scalar; arrays compare via contains ops. */
function asScalar(v: unknown): Scalar | undefined {
  if (v === undefined || v === null) return undefined;
  if (Array.isArray(v)) return undefined;
  return v as Scalar;
}

function asNumber(v: unknown): number | undefined {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) {
    return Number(v);
  }
  return undefined;
}

function toList(v: unknown): Array<string | number> {
  if (Array.isArray(v)) return v as Array<string | number>;
  if (v === undefined || v === null) return [];
  return [v as string | number];
}

/** Loose equality so config values ("yes") match typed answers consistently. */
function looseEq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  return String(a) === String(b);
}

export function evalCondition(cond: Condition, answers: Answers): boolean {
  const actual = answers[cond.field];

  switch (cond.op) {
    case "is_empty":
      return isEmpty(actual);
    case "is_not_empty":
      return !isEmpty(actual);

    case "equals":
      return looseEq(asScalar(actual), cond.value);
    case "not_equals":
      return !looseEq(asScalar(actual), cond.value);

    case "in":
      return toList(cond.value).some((v) => looseEq(v, asScalar(actual)));
    case "not_in":
      return !toList(cond.value).some((v) => looseEq(v, asScalar(actual)));

    case "contains":
      // array actual: membership; string actual: substring.
      if (Array.isArray(actual)) {
        return toList(cond.value).some((v) =>
          (actual as unknown[]).some((a) => looseEq(a, v)),
        );
      }
      return typeof actual === "string" && typeof cond.value === "string"
        ? actual.includes(cond.value)
        : false;
    case "not_contains":
      return !evalCondition({ ...cond, op: "contains" }, answers);

    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      const a = asNumber(actual);
      const b = asNumber(cond.value);
      if (a === undefined || b === undefined) return false;
      if (cond.op === "gt") return a > b;
      if (cond.op === "gte") return a >= b;
      if (cond.op === "lt") return a < b;
      return a <= b;
    }

    default: {
      // Exhaustiveness: a new CompareOp must be handled above.
      const _never: never = cond.op;
      throw new Error(`unhandled condition op: ${String(_never)}`);
    }
  }
}

/** A group matches when EVERY condition matches (AND). Empty group = true. */
export function matchGroup(group: ConditionGroup, answers: Answers): boolean {
  return group.every((c) => evalCondition(c, answers));
}
